import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import workOrderService from "../../services/workOrderService";
import WorkOrder from "../../models/workOrder";
import workOrderStatusHelper from "../../models/workOrderStatusHelper";
import SubscriptionContext from "../../contexts/subscriptionContext";
import SubscriptionContextModel from "../../contexts/subscriptionContextModel";
import WorkOrderLink from "../../components/work/work-order/WorkOrderLink";
import AccountLink from "../../components/work/AccountLink";
import workOrderTypeHelper from "../../models/workOrderTypeHelper";
import WorkOrderFilters from "../../models/workOrderFilters";
import Page from "../../components/work/layout/Page";
import SearchBar from "../../components/work/layout/SearchBar";

const WorkOrders: React.FC = () => {
  const navigate = useNavigate();

  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [hasParentWorkOrder, setHasParentWorkOrder] = useState<boolean>();

  const [showNotAvailableAnymore, setShowNotAvailableAnymore] =
    useState<boolean>(false);

  const { subscription } =
    useContext<SubscriptionContextModel>(SubscriptionContext);

  useEffect(() => {
    const init = async () => {
      const workOrderFilters = new WorkOrderFilters();
      workOrderFilters.showNotAvailableAnymore = showNotAvailableAnymore;

      const { data: workOrders } = await workOrderService.getWorkOrders(
        subscription.idSubscription,
        workOrderFilters,
      );
      setWorkOrders(workOrders);

      if (workOrders.length > 0) {
        const first = workOrders[0];

        let hasParent = false;

        if (first.parentWorkOrderNumber) {
          hasParent = true;
        }

        setHasParentWorkOrder(hasParent);
      }
    };

    init();
  }, [subscription, showNotAvailableAnymore]);

  const onCreateWorkOrderClick = () => {
    navigate("/work/work-orders/edit/new");
  };

  return (
    <Page
      pageTitle="Work Orders"
      breadcrumbs={[{ linkName: "Work Orders", linkUlr: "" }]}
    >
      <section className="section">
        <div className="card">
          {/* <div className="card-header">
            <div className="d-flex align-items-center">
              <form
                className="search-form"
                action="search-results.html"
                method="GET"
              >
                <i className="bi bi-search search-icon"></i>
                <input
                  type="search"
                  name="q"
                  placeholder="Search work orders..."
                  autoComplete="off"
                />
              </form>

              <div className="dropdown ms-3">
                <button
                  className="btn btn-outline-secondary btn-sm dropdown-toggle"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="bi bi-funnel me-1"></i>Filter
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <div className="dropdown-item">
                      <div className="d-flex align-items-center">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={showNotAvailableAnymore}
                          id="isShowNotAvailableAnymore"
                          onChange={(e) => {
                            setShowNotAvailableAnymore(
                              e.target.checked ? true : false,
                            );
                          }}
                        />
                        <label
                          className="ms-2"
                          htmlFor="isShowNotAvailableAnymore"
                        >
                          Show Not Available Anymore
                        </label>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <div className="d-flex flex-column align-items-end">
              {subscription.canCreateWorkOrders && (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={() => onCreateWorkOrderClick()}
                  >
                    <i className="bi bi-plus-lg me-1"></i> Create Work Order
                  </button>
                </>
              )}
              <div
                style={{
                  visibility: workOrders.length > 0 ? "visible" : "hidden",
                }}
              >{`${workOrders.length} work orders`}</div>
            </div>
          </div> */}

          <SearchBar
            placeHolderText="Search work orders..."
            searchValue={""}
            onSearchChange={() => {}}
            hasCreate={subscription.canCreateWorkOrders}
            createTitle="Create Work Order"
            onCreate={() => onCreateWorkOrderClick()}
            count={workOrders.length}
            countType="work orders"
            searchSideContent={
              <div className="dropdown">
                <button
                  className="btn btn-outline-secondary btn-sm dropdown-toggle"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="bi bi-funnel me-1"></i>Filter
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <div className="dropdown-item">
                      <div className="d-flex align-items-center">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={showNotAvailableAnymore}
                          id="isShowNotAvailableAnymore"
                          onChange={(e) => {
                            setShowNotAvailableAnymore(
                              e.target.checked ? true : false,
                            );
                          }}
                        />
                        <label
                          className="ms-2"
                          htmlFor="isShowNotAvailableAnymore"
                        >
                          Show Not Available Anymore
                        </label>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            }
          />

          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>Work Order #</th>
                    <th>Location</th>
                    <th>Trade</th>
                    <th>Work Order Type</th>
                    <th>Status</th>
                    {hasParentWorkOrder && <th>Reference Work Order #</th>}
                    {!hasParentWorkOrder && subscription.canSeeAccounts && (
                      <th>Account</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {workOrders.map((workOrder: WorkOrder) => (
                    <tr key={workOrder.workOrderNumber}>
                      <td>
                        <WorkOrderLink
                          workOrderNumber={workOrder.workOrderNumber}
                          canNavigate={true}
                        />
                      </td>
                      <td>{workOrder.location.locationName}</td>
                      <td>{workOrder.trade.tradeName}</td>
                      <td>
                        {workOrderTypeHelper.getDisplayValue(
                          workOrder.workOrderType,
                        )}
                      </td>
                      <td>
                        <span
                          className={workOrderStatusHelper.getBadgeClass(
                            workOrder.workOrderStatus,
                          )}
                        >
                          {workOrderStatusHelper.getDisplayValue(
                            workOrder.workOrderStatus,
                          )}
                        </span>
                      </td>
                      {hasParentWorkOrder && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <WorkOrderLink
                            workOrderNumber={workOrder.parentWorkOrderNumber}
                            canNavigate={workOrder.parentWorkOrder !== null}
                          />
                        </td>
                      )}
                      {!hasParentWorkOrder && subscription.canSeeAccounts && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <AccountLink account={workOrder.account} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </Page>
  );
};

export default WorkOrders;
